/**
 * Shared notification-channel parsing for Alert History and Dashboard rows
 * (must match per-alert API: `notify_by`, `notification_by_channel`, legacy `action_types`).
 */

/**
 * @param {unknown[]} raw
 * @returns {string[]}
 */
export function normalizeNotifyChannelStrings(raw) {
  const list = Array.isArray(raw) ? raw : [];
  return list.map((t) => {
    const u = String(t || "").toUpperCase();
    if (u === "SMS" || u === "TEXT") return "SMS";
    if (u === "EMAIL" || u === "MAIL") return "Email";
    if (u === "TELEGRAM" || u === "TG") return "Telegram";
    if (u === "HTTP" || u === "WEBHOOK") return "HTTP";
    return String(t || "").replace(/^\w/, (c) => c.toUpperCase());
  });
}

/**
 * Same priority as Alert History `notifyChannelsFromAlert`.
 * @param {Record<string, unknown> | null | undefined} apiAlert
 * @returns {string[]}
 */
export function notifyChannelsFromAlertPayload(apiAlert) {
  if (!apiAlert || typeof apiAlert !== "object") return [];
  if (
    Array.isArray(apiAlert.action_types) &&
    apiAlert.action_types.length > 0
  ) {
    return normalizeNotifyChannelStrings(apiAlert.action_types);
  }
  if (
    Array.isArray(apiAlert.actionTypes) &&
    apiAlert.actionTypes.length > 0
  ) {
    return normalizeNotifyChannelStrings(apiAlert.actionTypes);
  }
  const nbc = apiAlert.notification_by_channel;
  if (nbc && typeof nbc === "object" && !Array.isArray(nbc)) {
    const keys = Object.keys(nbc);
    if (keys.length > 0) return normalizeNotifyChannelStrings(keys);
  }
  if (apiAlert.notify_by != null && String(apiAlert.notify_by).trim() !== "") {
    const raw = String(apiAlert.notify_by).trim();
    const parts = raw
      .split(/[,;|]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return normalizeNotifyChannelStrings(parts.length > 0 ? parts : [raw]);
  }
  return [];
}

/** @typedef {'success' | 'failed' | 'partial'} NotifyChannelOutcome */

/**
 * @param {unknown} val
 * @returns {NotifyChannelOutcome}
 */
function classifyChannelOutcome(val) {
  if (val === true) return "success";
  if (val === false) return "failed";
  if (typeof val === "string") {
    const s = val.toLowerCase().trim();
    if (!s) return "partial";
    if (s.includes("fail") || s === "error") return "failed";
    if (s.includes("partial")) return "partial";
    if (
      s.includes("sent") ||
      s.includes("success") ||
      s.includes("deliver") ||
      s === "ok"
    ) {
      return "success";
    }
    if (s.includes("queue") || s === "pending" || s.includes("progress")) {
      return "partial";
    }
    return "partial";
  }
  if (val && typeof val === "object" && !Array.isArray(val)) {
    const o = /** @type {Record<string, unknown>} */ (val);
    const s = String(
      o.status ?? o.state ?? o.delivery_status ?? o.result ?? "",
    )
      .toLowerCase()
      .trim();
    if (s.includes("fail") || s === "error") return "failed";
    if (s.includes("partial")) return "partial";
    if (
      s.includes("sent") ||
      s.includes("success") ||
      s.includes("deliver") ||
      s === "ok"
    ) {
      return "success";
    }
    if (s.includes("queue") || s === "pending" || s.includes("progress")) {
      return "partial";
    }
    if (o.ok === true) return "success";
    if (o.ok === false) return "failed";
  }
  return "success";
}

/**
 * @param {Record<string, unknown> | null | undefined} apiAlert
 * @returns {Record<string, unknown> | null}
 */
function firstNonEmptyNotificationByChannel(apiAlert) {
  if (!apiAlert || typeof apiAlert !== "object") return null;
  const a = apiAlert.notification_by_channel;
  if (
    a &&
    typeof a === "object" &&
    !Array.isArray(a) &&
    Object.keys(a).length > 0
  ) {
    return a;
  }
  const ej =
    apiAlert.event_json && typeof apiAlert.event_json === "object"
      ? apiAlert.event_json
      : null;
  const b = ej?.notification_by_channel;
  if (
    b &&
    typeof b === "object" &&
    !Array.isArray(b) &&
    Object.keys(b).length > 0
  ) {
    return b;
  }
  return null;
}

/**
 * Merge `notification_by_channel` outcomes with the alert's channel list.
 * @param {Record<string, unknown>} apiAlert
 * @returns {{ label: string; outcome: NotifyChannelOutcome }[] | null}
 */
function parseNotificationByChannelMerged(apiAlert) {
  const nbc = firstNonEmptyNotificationByChannel(apiAlert);
  if (!nbc) return null;

  /** @type {Map<string, NotifyChannelOutcome>} */
  const outcomeByLabel = new Map();
  for (const [rawKey, val] of Object.entries(nbc)) {
    const label =
      normalizeNotifyChannelStrings([rawKey])[0] || String(rawKey).trim();
    if (!label) continue;
    outcomeByLabel.set(label.toLowerCase(), classifyChannelOutcome(val));
  }
  if (outcomeByLabel.size === 0) return null;

  const allChannels = notifyChannelsFromAlertPayload(apiAlert);
  const labelsFromNbc = Object.keys(nbc)
    .map((k) => normalizeNotifyChannelStrings([k])[0] || k)
    .filter(Boolean);
  const labels =
    allChannels.length > 0 ? allChannels : labelsFromNbc;
  if (labels.length === 0) return null;

  return labels.map((label) => ({
    label,
    outcome: outcomeByLabel.get(label.toLowerCase()) ?? "success",
  }));
}

/**
 * @param {string[]} channels
 * @param {unknown} notificationStatus
 * @param {unknown} [eventJsonStatus]
 * @returns {{ label: string; outcome: NotifyChannelOutcome }[]}
 */
export function buildNotifyChannelItemsFromAggregate(
  channels,
  notificationStatus,
  eventJsonStatus,
) {
  const list = Array.isArray(channels) ? channels : [];
  const ns = String(notificationStatus ?? "")
    .trim()
    .toLowerCase();
  const ns2 = String(eventJsonStatus ?? "")
    .trim()
    .toLowerCase();
  const agg = ns || ns2;
  if (list.length === 0) return [];
  if (agg.includes("fail") && !agg.includes("partial")) {
    return list.map((label) => ({ label, outcome: "failed" }));
  }
  if (agg.includes("partial")) {
    return list.map((label) => ({ label, outcome: "partial" }));
  }
  if (
    agg.includes("sent") ||
    agg.includes("success") ||
    agg === "delivered"
  ) {
    return list.map((label) => ({ label, outcome: "success" }));
  }
  if (agg.includes("queue") || agg.includes("pending")) {
    return list.map((label) => ({ label, outcome: "partial" }));
  }
  return list.map((label) => ({ label, outcome: "success" }));
}

/**
 * Per-channel delivery for "Notified by" chips (Dashboard / History / SSE).
 * Uses `notification_by_channel` when present; otherwise falls back to aggregate `notification_status`.
 * @param {Record<string, unknown> | null | undefined} apiAlert
 * @returns {{ label: string; outcome: NotifyChannelOutcome }[]}
 */
export function buildNotifyChannelItems(apiAlert) {
  const merged = parseNotificationByChannelMerged(apiAlert);
  if (merged && merged.length > 0) return merged;
  const eventJson =
    apiAlert?.event_json && typeof apiAlert.event_json === "object"
      ? apiAlert.event_json
      : {};
  return buildNotifyChannelItemsFromAggregate(
    notifyChannelsFromAlertPayload(apiAlert),
    apiAlert?.notification_status,
    eventJson.notification_status,
  );
}

/**
 * Props-style resolver for table rows that may carry precomputed `notifyChannelItems`.
 * @param {{
 *   notifyChannelItems?: { label: string; outcome: NotifyChannelOutcome }[];
 *   channels?: string[];
 *   notifyChannels?: string[];
 *   notificationStatus?: unknown;
 *   notificationStatusRaw?: unknown;
 * }} rowLike
 */
export function resolveNotifyChipItems(rowLike) {
  if (!rowLike || typeof rowLike !== "object") return [];
  const pre = rowLike.notifyChannelItems;
  if (Array.isArray(pre) && pre.length > 0) return pre;
  const ch = Array.isArray(rowLike.notifyChannels)
    ? rowLike.notifyChannels
    : Array.isArray(rowLike.channels)
      ? rowLike.channels
      : [];
  const status =
    rowLike.notificationStatus ?? rowLike.notificationStatusRaw ?? "";
  return buildNotifyChannelItemsFromAggregate(ch, status, undefined);
}
